import { FC, useCallback, useEffect, useState } from "react";
import classes from './tutorFinance.module.scss'
import { ITutorFinance, tutorService } from "../../../entities/tutor";
import { useAppSelector } from "../../../app/store/store";
import { FEATURES, hasFeature, useMyActions } from "../../../entities/my";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { MyError } from "../../../shared/lib/error/MyError";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { getDateUTC } from "../../../shared/lib/helpers/getDateUTC";
import { Calendar } from "../../../shared/ui/calendar";
import { LoaderSpinner } from "../../../shared/ui/spinner";
import { FinanceInfo } from './FinanceInfo';


interface IProps {
    id: number;
    userId?: number;
    revision?: number;
}

export const TutorFinance: FC<IProps> = ({id, userId, revision = 0}) => {

    const [finance, setFinance] = useState<ITutorFinance | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [range, setRange] = useState<{from: string; to: string} | null>(null)
    const {my} = useAppSelector(s => s.myReducer)
    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()

    const getData = useCallback(async (startDate: string, endDate: string) => {
        try{
            setIsLoading(true)
            const financeRes = userId && userId > 0
                ? await tutorService.getFinanceByUserId(userId, startDate, endDate)
                : await tutorService.getFinance(id, startDate, endDate)
            setFinance(financeRes)
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при получении финансов исполнителя', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }, [id, userId, setGlobalMessage, setIsAuth])

    const setDate = (startDate: Date | null, endDate: Date | null) => {
        if(startDate && endDate){
            setRange({from: getDateUTC(startDate), to: getDateUTC(endDate)})
        }
        if(!startDate && !endDate){
            setRange(null)
            setFinance(null)
        }
    }

    useEffect(() => {
        if(range) void getData(range.from, range.to)
    }, [range, revision, getData])

    return (
        <section className={classes.container}>
            <Calendar
                onDateRangeSelect={setDate} 
                isLoading={isLoading}
            />
            {
                isLoading
                    ?
                <section className={classes.loader}><LoaderSpinner /></section>
                    :
                finance
                    &&
                <section className={classes.data}>
                    <section className={classes.sub}>Исполнитель</section>
                    <div className={classes.metric}>
                        <span>Количество часов: {finance.hoursCount}</span>
                        <FinanceInfo label="Количество часов" description="Количество завершённых занятий за выбранный период в часах: количество минут ÷ 60. Период определяется по дате завершения занятия." />
                    </div>
                    <div className={classes.metric}>
                        <span>Вознаграждение: {finance.accruals} ₽</span>
                        <FinanceInfo label="Вознаграждение" description="Все начисления за выбранный период: уроки + премии − штрафы. Выплачено или нет тут не учитывается" />
                    </div>
                    <div className={classes.metric}>
                        <span>Баланс: {finance.wages} ₽</span>
                        <FinanceInfo label="Баланс" description="Сумма неоплаченных начислений за выбранный период: уроки + премии − штрафы. Считается только НЕ оплаченная сумма" />
                    </div>
                    {
                        hasFeature(my, FEATURES.FINANCE.VIEW_FULL)
                            &&
                        <>
                            <section className={classes.sub}>Общее</section>
                            <span>Прибыль: {finance.amount} ₽</span>
                        </>
                    }
                </section>
            }
        </section>
    )
}
